/**
 * Accounting > Payroll Records. What each person cost, read off ADP's own "Payroll
 * Details" report, so a pay question no longer starts with a hunt for a PDF.
 *
 * Second report type (Mercedes, Nov 3, 2026): RUN's "Employee Paid Time Off", PDF or
 * Excel. Same drop zone; the server tells the two apart. Its balances show below, in the
 * Payroll Planner and on the Attendance page, as of that payroll's pay date.
 *
 * Every employee's pay is on this page. Who may see it is decided on the server
 * (payroll.js: Greg and Mercedes); the sidebar hides it from everybody else as a courtesy.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { payrollRecords } from '../services/api';

const money = (v) => (v == null || !isFinite(Number(v)) ? '' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const hrs = (v) => (v ? Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '');
const day = (d) => {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
// Hours, with a negative balance in parentheses: (0.14).
const bal = (v) => (v == null || !isFinite(Number(v)) ? '' : Number(v) < 0
  ? `(${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
  : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const span = (r) => (r.is_range ? `${day(r.check_from)} to ${day(r.check_to)}` : day(r.check_to));
const th = { textAlign: 'right', padding: '6px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--gray-500, #6b7280)', whiteSpace: 'nowrap' };
const td = { textAlign: 'right', padding: '7px 10px', fontSize: 13.5, whiteSpace: 'nowrap' };
const left = { textAlign: 'left' };

const toBase64 = (file) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
  fr.onerror = () => reject(new Error('Could not read that file.'));
  fr.readAsDataURL(file);
});

export default function PayrollRecords() {
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [ptoReports, setPtoReports] = useState([]);
  const [ptoOpen, setPtoOpen] = useState(null);
  const [ptoConfirm, setPtoConfirm] = useState(null);
  const input = useRef(null);

  const show = useCallback(async (id) => {
    setErr('');
    try { setOpen(await payrollRecords('get', { id })); } catch (e) { setErr(e.message || 'Could not open that report.'); }
  }, []);

  const showPto = useCallback(async (id) => {
    setErr('');
    try { setPtoOpen(await payrollRecords('pto-get', { id })); } catch (e) { setErr(e.message || 'Could not open that time off report.'); }
  }, []);

  const load = useCallback(async () => {
    setErr('');
    try {
      const d = await payrollRecords('list');
      setReports(d.reports || []);
      setPtoReports(d.ptoReports || []);
      if (d.reports && d.reports.length) await show(d.reports[0].id);
      if (d.ptoReports && d.ptoReports.length) await showPto(d.ptoReports[0].id);
    } catch (e) { setErr(e.message || 'Could not load payroll records.'); }
    setLoading(false);
  }, [show, showPto]);

  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setBusy(true); setErr(''); setNote('');
    const done = []; const failed = [];
    for (const f of list) {
      try {
        if (!/\.(pdf|xlsx|xls|csv)$/i.test(f.name)) throw new Error('Not a PDF or a spreadsheet. Download the report from ADP as a PDF (the time off report can be Excel too).');
        const d = await payrollRecords('upload', { filename: f.name, fileBase64: await toBase64(f) });
        if (d.kind === 'pto') {
          setPtoReports(d.ptoReports || []); setPtoOpen(d.saved);
          done.push(`time off balances as of ${day(d.saved.check_date)}`);
        } else {
          setReports(d.reports || []); setOpen(d.saved);
          done.push(span(d.saved));
        }
      } catch (e) { failed.push(`${f.name}: ${e.message || 'did not save'}`); }
    }
    if (done.length) setNote(`Saved ${done.join(', ')}. Each report is checked against its own totals before it is saved.`);
    if (failed.length) setErr(failed.join(' '));
    setBusy(false);
    if (input.current) input.current.value = '';
  };

  const remove = async (id) => {
    setBusy(true); setErr(''); setNote('');
    try {
      const d = await payrollRecords('delete', { id });
      setReports(d.reports || []); setConfirmId(null);
      if (open && open.id === id) setOpen(null);
    } catch (e) { setErr(e.message || 'Could not remove that report.'); }
    setBusy(false);
  };

  const removePto = async (id) => {
    setBusy(true); setErr(''); setNote('');
    try {
      const d = await payrollRecords('pto-delete', { id });
      setPtoReports(d.ptoReports || []); setPtoConfirm(null);
      if (ptoOpen && ptoOpen.id === id) setPtoOpen(null);
    } catch (e) { setErr(e.message || 'Could not remove that report.'); }
    setBusy(false);
  };

  const lines = (open && open.lines) || [];
  const cost = (l) => Number(l.gross) + Number(l.employer);

  return (
    <div style={{ padding: 24, maxWidth: 1180 }}>
      <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>Payroll Records</h1>
      <p style={{ margin: '0 0 18px', color: 'var(--gray-600, #4b5563)', maxWidth: 760 }}>
        Each pay run, download the Payroll Details report from ADP as a PDF and drop it here. The Employee
        Paid Time Off report goes in the same place (PDF or Excel) and shows each person's PTO and sick hours
        left. Every report is checked against its own totals before it is saved. Only Greg and Mercedes can open this page.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); upload(e.dataTransfer.files); }}
        style={{ border: `2px dashed ${over ? '#DC582A' : '#c5d0da'}`, borderRadius: 8, padding: '18px 20px', background: over ? '#fff6f1' : '#f8fafc', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}
      >
        <Upload size={20} color="#00395C" />
        <span style={{ flex: '1 1 260px' }}>{busy ? 'Reading the report…' : 'Drop a Payroll Details PDF (one pay run or a date range) or an Employee Paid Time Off report here.'}</span>
        <button type="button" disabled={busy} onClick={() => input.current && input.current.click()}
          style={{ background: '#00395C', color: '#fff', border: 0, borderRadius: 6, padding: '8px 14px', fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
          Choose file
        </button>
        <input id="payroll-file" ref={input} type="file" accept="application/pdf,.pdf,.xlsx,.xls,.csv" multiple hidden onChange={(e) => upload(e.target.files)} />
      </div>

      {note && <div style={{ background: '#e8f5ec', color: '#1c7c43', padding: '9px 12px', borderRadius: 6, marginBottom: 12 }}>{note}</div>}
      {err && <div style={{ background: '#fdeaea', color: '#b42318', padding: '9px 12px', borderRadius: 6, marginBottom: 12 }}>{err}</div>}
      {loading && <p>Loading…</p>}
      {!loading && !reports.length && !err && <p style={{ color: 'var(--gray-600, #4b5563)' }}>No reports yet. The first one you drop in will show here.</p>}

      {reports.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 22, background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
            <thead><tr>
              <th style={{ ...th, ...left }}>Pay date</th><th style={{ ...th, ...left }}>Covers</th><th style={th}>People</th>
              <th style={th}>Pay</th><th style={th}>Employer cost</th><th style={th}>Total</th><th style={{ ...th, ...left }}>Added</th><th style={th} />
            </tr></thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #eef0f2', background: open && open.id === r.id ? '#eef4fa' : undefined }}>
                  <td style={{ ...td, ...left }}>
                    <button type="button" onClick={() => show(r.id)} style={{ background: 'none', border: 0, padding: 0, color: '#00395C', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>{span(r)}</button>
                  </td>
                  <td style={{ ...td, ...left }}>{r.is_range ? 'Date range, summed' : `Work from ${day(r.period_from)} to ${day(r.period_to)}`}</td>
                  <td style={td}>{r.employees}</td>
                  <td style={td}>{money(r.gross)}</td>
                  <td style={td}>{money(r.employer)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{money(r.gross + r.employer)}</td>
                  <td style={{ ...td, ...left }}>{day(r.uploaded_on)}{r.uploaded_by ? ` by ${r.uploaded_by}` : ''}</td>
                  <td style={td}>
                    {confirmId === r.id ? (
                      <span>
                        <button type="button" disabled={busy} onClick={() => remove(r.id)} style={{ background: '#b42318', color: '#fff', border: 0, borderRadius: 4, padding: '3px 8px', cursor: 'pointer', marginRight: 6 }}>Remove</button>
                        <button type="button" onClick={() => setConfirmId(null)} style={{ background: 'none', border: '1px solid #c5d0da', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>Keep</button>
                      </span>
                    ) : (
                      <button type="button" title="Remove this report" aria-label={`Remove the report for ${span(r)}`} onClick={() => setConfirmId(r.id)} style={{ background: 'none', border: 0, cursor: 'pointer', color: '#6b7280' }}><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <>
          <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>{open.is_range ? `Totals for ${span(open)}` : `Pay date ${span(open)}`}</h2>
          <p style={{ margin: '0 0 10px', color: 'var(--gray-600, #4b5563)', maxWidth: 760 }}>
            Cost is pay plus employer taxes and the retirement match. {open.is_range ? 'A date range is summed per person, so it does not show how many pay runs each person worked.' : ''}
          </p>
          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
              <thead><tr>
                <th style={{ ...th, ...left }}>Person</th><th style={{ ...th, ...left }}>Department</th><th style={th}>Rate</th><th style={th}>Hours</th>
                <th style={th}>Overtime hrs</th><th style={th}>Time off hrs</th><th style={th}>Pay</th><th style={th}>Of which bonus</th><th style={th}>Employer cost</th><th style={th}>Total cost</th>
              </tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.employee} style={{ borderTop: '1px solid #eef0f2' }}>
                    <td style={{ ...td, ...left, fontWeight: 600 }}>{l.display_name}</td>
                    <td style={{ ...td, ...left, whiteSpace: 'normal' }}>{l.departments}</td>
                    <td style={td}>{l.rate ? money(l.rate) : 'Salary'}</td>
                    <td style={td}>{hrs(l.hours)}</td>
                    <td style={td}>{hrs(l.ot_hours)}</td>
                    <td style={td}>{hrs(l.pto_hours)}</td>
                    <td style={td}>{money(l.gross)}</td>
                    <td style={td}>{l.bonus ? money(l.bonus) : ''}</td>
                    <td style={td}>{money(l.employer)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{money(cost(l))}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #00395C' }}>
                  <td style={{ ...td, ...left, fontWeight: 700 }} colSpan={6}>Total</td>
                  <td style={{ ...td, fontWeight: 700 }}>{money(open.gross)}</td>
                  <td style={td}>{money(lines.reduce((s, l) => s + Number(l.bonus), 0)) }</td>
                  <td style={{ ...td, fontWeight: 700 }}>{money(open.employer)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{money(open.gross + open.employer)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2 style={{ fontSize: 17, margin: '30px 0 4px' }}>Paid time off balances</h2>
      <p style={{ margin: '0 0 10px', color: 'var(--gray-600, #4b5563)', maxWidth: 760 }}>
        From RUN's Employee Paid Time Off report, as of each payroll's pay date. PTO is the Vacation plan in RUN.
        Left is after the hours taken in that payroll. The Payroll Planner flags PTO or sick hours entered above what is left.
      </p>
      {!loading && !ptoReports.length && <p style={{ color: 'var(--gray-600, #4b5563)' }}>No time off reports yet. Drop one in above.</p>}
      {ptoReports.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 18, background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
            <thead><tr>
              <th style={{ ...th, ...left }}>Balances as of</th><th style={{ ...th, ...left }}>Covers</th><th style={th}>People</th>
              <th style={{ ...th, ...left }}>Checked</th><th style={{ ...th, ...left }}>Added</th><th style={th} />
            </tr></thead>
            <tbody>
              {ptoReports.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #eef0f2', background: ptoOpen && ptoOpen.id === r.id ? '#eef4fa' : undefined }}>
                  <td style={{ ...td, ...left }}>
                    <button type="button" onClick={() => showPto(r.id)} style={{ background: 'none', border: 0, padding: 0, color: '#00395C', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>{day(r.check_date)}</button>
                  </td>
                  <td style={{ ...td, ...left }}>{r.period_from ? `Work from ${day(r.period_from)} to ${day(r.period_to)}` : ''}</td>
                  <td style={td}>{r.employees}</td>
                  <td style={{ ...td, ...left }}>{r.tied ? 'Ties to its totals' : 'Spreadsheet without totals, not checked'}</td>
                  <td style={{ ...td, ...left }}>{day(r.uploaded_on)}{r.uploaded_by ? ` by ${r.uploaded_by}` : ''}</td>
                  <td style={td}>
                    {ptoConfirm === r.id ? (
                      <span>
                        <button type="button" disabled={busy} onClick={() => removePto(r.id)} style={{ background: '#b42318', color: '#fff', border: 0, borderRadius: 4, padding: '3px 8px', cursor: 'pointer', marginRight: 6 }}>Remove</button>
                        <button type="button" onClick={() => setPtoConfirm(null)} style={{ background: 'none', border: '1px solid #c5d0da', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>Keep</button>
                      </span>
                    ) : (
                      <button type="button" title="Remove this report" aria-label={`Remove the time off report for ${day(r.check_date)}`} onClick={() => setPtoConfirm(r.id)} style={{ background: 'none', border: 0, cursor: 'pointer', color: '#6b7280' }}><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {ptoOpen && (
        <>
          <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Hours as of {day(ptoOpen.check_date)}</h3>
          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
              <thead><tr>
                <th style={{ ...th, ...left }}>Person</th>
                <th style={{ ...th, ...left }}>PTO plan year</th><th style={th}>PTO available</th><th style={th}>PTO taken</th><th style={th}>PTO left</th>
                <th style={th}>Sick available</th><th style={th}>Sick taken</th><th style={th}>Sick left</th>
                <th style={th}>Taken this payroll</th>
              </tr></thead>
              <tbody>
                {(ptoOpen.people || []).map((p) => {
                  const now = Number((p.pto && p.pto.periodTaken) || 0) + Number((p.sick && p.sick.periodTaken) || 0);
                  return (
                    <tr key={p.employee} style={{ borderTop: '1px solid #eef0f2' }}>
                      <td style={{ ...td, ...left, fontWeight: 600 }}>{p.name}</td>
                      <td style={{ ...td, ...left }}>{p.pto && p.pto.from ? `${day(p.pto.from)} to ${day(p.pto.to)}` : ''}</td>
                      <td style={td}>{p.pto ? bal(p.pto.available) : ''}</td>
                      <td style={td}>{p.pto ? bal(p.pto.taken) : ''}</td>
                      <td style={{ ...td, fontWeight: 600, color: p.pto && p.pto.balance < 0 ? '#b42318' : undefined }}>{p.pto ? bal(p.pto.balance) : 'Not in plan'}</td>
                      <td style={td}>{p.sick ? bal(p.sick.available) : ''}</td>
                      <td style={td}>{p.sick ? bal(p.sick.taken) : ''}</td>
                      <td style={{ ...td, fontWeight: 600, color: p.sick && p.sick.balance < 0 ? '#b42318' : undefined }}>{p.sick ? bal(p.sick.balance) : 'Not in plan'}</td>
                      <td style={td}>{now ? hrs(now) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
